import { Box, Divider, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { StyledSelect } from '@tassat/formik-input';
import styles from '@tassat/formik-input/src/StyledSelect/styles';
import React from 'react';
import { SingleValue } from 'react-select';

import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';

import type { Option, SelectOption } from './types';

const customStyles = (theme: ApplicationMuiTheme) => ({
    valueContainer: (base) => ({
        ...base,
        cursor: 'text',
        height: '21px',
        width: '100%'
    }),
    control: (base, state) => ({
        ...base,
        border: state.isFocused ? `2px solid ${theme?.app?.clientTheme.colors.primaryColor || '#375073'}` : '1px solid',
        fontWeight: 500,
        lineHeight: '18px',
        color: theme?.app?.applicationColors?.fontLabelColor || '#727272',
        boxShadow: 'none',
        '&:hover': {
            borderColor: `${theme?.app?.clientTheme.colors.primaryColor || '#375073'}`,
            borderWidth: '2px'
        }
    }),
    singleValue: (base) => ({
        ...base,
        fontWeight: 500,
        lineHeight: '18px',
        color: theme?.app?.applicationColors?.fontLabelColor || '#727272',
        marginTop: '4px !important',
        marginLeft: '-1px'
    }),
    input: (base) => ({
        ...base,
        '[type="text"]': {
            fontSize: theme.typography.body2.fontSize + ' !important',
            fontWeight: 500,
            lineHeight: '18px',
            color: (theme?.app?.applicationColors?.fontLabelColor || '#727272') + ' !important'
        },
        marginLeft: '-1px'
    }),
    option: (base, state) => ({
        ...base,
        ...styles(theme)
            .option(base, state),
        fontWeight: 500,
        textDecoration: 'underline',
        lineHeight: '18px',
        padding: theme.spacing(1)
    })
});

type ClientSelectProps = {
    options: Option[];
    value: SelectOption;
    onChange: (val: SingleValue<SelectOption>) => void;
};

type IGroupHeadingProps = {
    data: Option;
    id: string;
};

const useGroupHeadingStyles = makeStyles((theme: ApplicationMuiTheme) => {
    return {
        title: {
            fontWeight: 700,
            lineHeight: '14px',
            color: theme?.app?.applicationColors?.fontLabelColor || '#727272',
            padding: theme.spacing(1)
        }
    };
});

const GroupHeading: React.FC<IGroupHeadingProps> = React.forwardRef(({ data, id }: IGroupHeadingProps) => {
    const classes = useGroupHeadingStyles();
    const groupIndex = Number(id.split('-')[4]);

    return (
        <>
            {
                !!groupIndex &&
                <>
                    <Box
                        pl={1}
                        pr={1}
                    >
                        <Divider />
                    </Box>
                    <Box mt={1} />
                </>
            }
            <Typography
                className={classes.title}
                variant='body1'
            >
                {`${data.label}:`}
            </Typography>
        </>
    );
});
GroupHeading.displayName = 'AddressBookClientSelectGroupHeading';

const ClientSelect: React.FC<ClientSelectProps> = ({ options, value, onChange }) => {
    const theme = useTheme<ApplicationMuiTheme>();

    return (
        <StyledSelect
            autoComplete
            components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
                GroupHeading
            }}
            customStyles={customStyles(theme)}
            id='clientSelect'
            isClearable={false}
            isSearchable
            onChange={(val) => onChange(val)}
            options={options}
            placeholder=''
            value={value}
        />
    );
};

export default ClientSelect;